<script setup>
/**
 * The dashboard's filter row: status chips and a title search. Channel is not
 * a filter here — it is the route the sidebar navigates to, so the grid is
 * already scoped by the time this renders. Filtering itself happens in
 * `helpers/campaignFilters.js`; this component only owns the two v-models.
 */
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import { CAMPAIGN_STATUS_FILTERS } from 'dashboard/components-next/Campaigns/helpers/campaignFilters';

const status = defineModel('status', { type: String, required: true });
const searchQuery = defineModel('searchQuery', {
  type: String,
  required: true,
});

const { t } = useI18n();

const CHIP_BASE_CLASSES =
  'inline-flex items-center h-7 px-3 rounded-full font-medium text-label-small transition-colors';
const CHIP_ACTIVE_CLASSES =
  'bg-n-solid-active text-n-slate-12 shadow-sm outline outline-1 outline-n-container';
const CHIP_INACTIVE_CLASSES =
  'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12';

const statusOptions = Object.entries(CAMPAIGN_STATUS_FILTERS).map(
  ([key, value]) => ({
    value,
    label: t(`CAMPAIGN.ALL.FILTERS.STATUS.${key}`),
  })
);

const chipClasses = isActive => [
  CHIP_BASE_CLASSES,
  isActive ? CHIP_ACTIVE_CLASSES : CHIP_INACTIVE_CLASSES,
];
</script>

<template>
  <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
    <div
      role="group"
      :aria-label="t('CAMPAIGN.ALL.FILTERS.STATUS_GROUP_LABEL')"
      class="flex flex-wrap items-center gap-1.5"
    >
      <button
        v-for="option in statusOptions"
        :key="option.value"
        type="button"
        :class="chipClasses(status === option.value)"
        :aria-pressed="status === option.value"
        @click="status = option.value"
      >
        {{ option.label }}
      </button>
    </div>

    <Input
      v-model="searchQuery"
      type="search"
      size="sm"
      :placeholder="t('CAMPAIGN.ALL.FILTERS.SEARCH_PLACEHOLDER')"
      class="w-full sm:w-64 ltr:sm:ml-auto rtl:sm:mr-auto"
    />
  </div>
</template>
